import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment, TextField } from '@mui/material';
import styles from './team_management.module.css';

import { getAllUsers } from './functions/get_all_users.fucntion';

const CreateTeamModal = ({ isOpen, onClose, onSubmit, mode, initialData, title, buttonText }) => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    region: '',
  });
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [opsUsers, setOpsUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const cityDropdownRef = useRef(null);
  const regionDropdownRef = useRef(null);

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        name: initialData.name || '',
        city: initialData.city || '',
        region: initialData.region || '',
      });

      // Pre-select members based on initialData
      if (initialData.members && initialData.members.length > 0) {
        // Keep the full member objects to maintain consistency with handleMemberToggle
        setSelectedMembers(initialData.members);
      } else {
        setSelectedMembers([]);
      }
    } else {
      // Reset for create mode
      setFormData({ name: '', city: '', region: '' });
      setSelectedMembers([]);
    }
  }, [mode, initialData]);

  const fetchData = async () => {
    try {
      const result = await getAllUsers();
      const responseArray = result?.response || [];

      if (responseArray.length > 0) {
        setOpsUsers(responseArray);
      }
    } catch (error) {
      console.error('Error fetching users data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [isOpen]);

  const cities = ['Lahore', 'Karachi', 'Islamabad', 'Faisalabad', 'Peshawar'];
  const regions = ['Central A', 'Central B', 'North', 'South'];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSingleSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMemberToggle = (member) => {
    setSelectedMembers((prev) => {
      // Use the same multi-strategy matching as the checkbox check
      const exists = prev.find((m) => {
        const idMatch = m.id === member.id;
        const emailMatch = m.email === member.email;
        const userIdMatch = m.user_id === member.id;
        return idMatch || emailMatch || userIdMatch;
      });

      if (exists) {
        // Remove the member using the same matching logic
        return prev.filter((m) => {
          const idMatch = m.id === member.id;
          const emailMatch = m.email === member.email;
          const userIdMatch = m.user_id === member.id;
          return !(idMatch || emailMatch || userIdMatch);
        });
      } else {
        // Add the member
        return [
          ...prev,
          {
            id: member.id,
            name: member.name,
            email: member.email,
            city: member.city,
            region: member.region,
          },
        ];
      }
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      const teamData = {
        ...formData,
        members: selectedMembers,
      };

      // Include team ID when in edit mode
      if (mode === 'edit' && initialData?.id) {
        teamData.id = initialData.id;
      }

      const result = await onSubmit(teamData);

      // Only close modal and reset fields if the operation was successful
      if (result && result.success) {
        handleModalClose();
      }
    } catch (error) {
      console.error('Error submitting team data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalClose = () => {
    setFormData({ name: '', city: '', region: '' });
    setSelectedMembers([]);
    setSearchTerm('');
    setCityDropdownOpen(false);
    setRegionDropdownOpen(false);
    setIsLoading(false);
    onClose();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setCityDropdownOpen(false);
      }
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target)) {
        setRegionDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredMembers = opsUsers.filter(
    (member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) {
    return null;
  }

  const generateInitials = (name) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const SingleSelectDropdown = ({
    label,
    options,
    selectedValue,
    isOpen,
    onToggle,
    onChange,
    placeholder,
    dropdownRef,
  }) => {
    return (
      <div className={styles['form-group']}>
        <label>{label}</label>
        <div className={styles['multi-select-container']} ref={dropdownRef}>
          <div className={styles['multi-select-field']} onClick={() => onToggle(!isOpen)}>
            <div className={styles['selected-values']}>
              {!selectedValue ? (
                <span className={styles['placeholder']}>{placeholder}</span>
              ) : (
                <span className={styles['selected-tag']}>
                  {selectedValue}
                  <button
                    className={styles['remove-tag']}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange('');
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            <div className={styles['dropdown-arrow']}>▼</div>
          </div>
          {isOpen && (
            <div className={styles['dropdown-options']}>
              {options.map((option) => (
                <div
                  key={option}
                  className={`${styles['dropdown-option']} ${
                    selectedValue === option ? styles['selected'] : ''
                  }`}
                  onClick={() => {
                    onChange(option);
                    onToggle(false);
                  }}
                >
                  <span>{option}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  SingleSelectDropdown.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedValue: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string.isRequired,
    dropdownRef: PropTypes.object,
  };

  return (
    <div className={styles['modal-overlay']} onClick={handleModalClose}>
      <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles['modal-header']}>
          <h2 className={styles['modal-title']}>{title}</h2>
          <button className={styles['close-button']} onClick={handleModalClose}>
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles['modal-body']}>
          {/* Left Section - Add Info */}
          <div className={styles['modal-section']}>
            <h3 className={styles['section-title']}>Add Info</h3>
            <div className={styles['form-group']}>
              <label>Name</label>
              <input
                type="text"
                placeholder="Enter"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <SingleSelectDropdown
              label="City"
              options={cities}
              selectedValue={formData.city}
              isOpen={cityDropdownOpen}
              onToggle={setCityDropdownOpen}
              onChange={(value) => handleSingleSelectChange('city', value)}
              placeholder="Select City"
              dropdownRef={cityDropdownRef}
            />
            <SingleSelectDropdown
              label="Region"
              options={regions}
              selectedValue={formData.region}
              isOpen={regionDropdownOpen}
              onToggle={setRegionDropdownOpen}
              onChange={(value) => handleSingleSelectChange('region', value)}
              placeholder="Select Region"
              dropdownRef={regionDropdownRef}
            />
          </div>

          {/* Right Section - Select Members */}
          <div className={styles['modal-section']}>
            <h3 className={styles['section-title']}>Select Members</h3>
            <TextField
              placeholder="Search"
              size="small"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                '& .MuiInputBase-input': {
                  color: '#999FA7',
                  '::placeholder': {
                    color: '#999FA7',
                    opacity: 1,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  border: '1px solid #ccc',
                  borderRadius: '10px',
                  paddingRight: 0,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#999FA7' }} />
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles['members-list']}>
              {filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <div key={member.id} className={styles['member-item']}>
                    <label className={styles['member-checkbox']}>
                      <input
                        type="checkbox"
                        checked={selectedMembers.some((m) => {
                          // Try multiple matching strategies
                          const idMatch = m.id === member.id;
                          const emailMatch = m.email === member.email;
                          // In case team members use user_id
                          const userIdMatch = m.user_id === member.id;
                          return idMatch || emailMatch || userIdMatch;
                        })}
                        onChange={() => handleMemberToggle(member)}
                      />
                      <span className={styles['checkmark']}></span>
                    </label>
                    <div className={styles['member-list-avatar']}>
                      {generateInitials(member.name)}
                    </div>
                    <div className={styles['member-info']}>
                      <div className={styles['member-name']}>{member.name}</div>
                      <div className={styles['member-email']}>{member.email}</div>
                    </div>
                    {/* <div className={styles['member-location']}>
                      <span className={styles['member-city']}>{member.city}</span>
                      <span className={styles['member-region']}>{member.region}</span>
                    </div> */}
                  </div>
                ))
              ) : (
                <div className={styles['no-members-found']}>
                  <p>No members found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className={styles['modal-footer']}>
          <button
            className={styles['cancel-button']}
            onClick={handleModalClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button className={styles['create-button']} onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CircularProgress size={16} color="inherit" />
                {mode === 'create' ? 'Creating...' : 'Updating...'}
              </div>
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

CreateTeamModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  initialData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    city: PropTypes.string,
    region: PropTypes.string,
    members: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
      })
    ),
  }),
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string.isRequired,
};

export default CreateTeamModal;
